import { useState, useEffect } from "react";
import { Calendar, MapPin, Clock, Users, Minus, Plus, ShoppingCart, Check, ChevronDown, Ticket, Heart, Award, Star, ExternalLink, Phone } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

import { API } from "../config/api";

// Slot and Category configuration - prices hidden from display, only used for checkout
const SLOTS = [
  { id: "A", name: "Slot A", time: "11:00 AM - 11:45 AM", enabled: true },
  { id: "B", name: "Slot B", time: "12:00 PM - 12:45 PM", enabled: false }, // Disabled for now
  { id: "C", name: "Slot C", time: "1:00 PM - 1:30 PM", enabled: false },   // Disabled for now
];

// Default ticket limits
const DEFAULT_LIMITS = {
  "A": { "A": 171, "B": 75, "C": 999 },
  "B": { "A": 171, "B": 75, "C": 999 },
  "C": { "A": 171, "B": 75, "C": 999 },
};

// ============================================================================
// TEMPORARY PRICE OVERRIDE - remove after production DB alignment
// This ensures production UI shows correct prices until Admin/DB sync is fixed
// Adult = $30, Kids = $25, Infants = FREE
// ============================================================================
const TEMP_PRICE_OVERRIDE = {
  "A": 30,  // Adult
  "B": 25,  // Kids
  "C": 0,   // Infant (FREE)
};
const USE_TEMP_PRICE_OVERRIDE = true; // Set to false once DB is aligned
// ============================================================================

// Category definitions (prices come from backend)
const CATEGORIES = [
  { id: "A", name: "Adult", description: "Adults (13+)", code: "adult" },
  { id: "B", name: "Kids", description: "Kids (5-12)", code: "kids" },
  { id: "C", name: "Infant", description: "Infants (0-4)", code: "infants" },
];

export default function EventPage() {
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [cart, setCart] = useState({});
  const [customerInfo, setCustomerInfo] = useState({ name: "", email: "", phone: "" });
  const [checkingOut, setCheckingOut] = useState(false);
  const [expandedSlot, setExpandedSlot] = useState(null);
  const [ticketAvailability, setTicketAvailability] = useState(null);

  useEffect(() => {
    fetchEventData();
    fetchTicketAvailability();
  }, []);

  const fetchTicketAvailability = async () => {
    try {
      const res = await fetch(`${API}/tickets/availability/vishu-2026`);
      if (res.ok) {
        const data = await res.json();
        setTicketAvailability(data.availability);
      }
    } catch (error) {
      console.error("Error fetching availability:", error);
    }
  };

  // Get remaining tickets for a slot/category
  const getRemainingTickets = (slotId, categoryId) => {
    if (ticketAvailability && ticketAvailability[slotId]) {
      const slotData = ticketAvailability[slotId];
      // Map category ID to ticket type code
      const codeMap = { "A": "adult", "B": "kids", "C": "infants" };
      const code = codeMap[categoryId] || categoryId.toLowerCase();
      
      if (slotData.ticket_types && slotData.ticket_types[code]) {
        return slotData.ticket_types[code].remaining;
      }
    }
    // Fallback to default limits
    return DEFAULT_LIMITS[slotId]?.[categoryId] || 999;
  };

  // Check if slot is visible
  const isSlotVisible = (slotId) => {
    if (ticketAvailability && ticketAvailability[slotId]) {
      return ticketAvailability[slotId].is_visible !== false;
    }
    // Fallback to SLOTS config
    const slot = SLOTS.find(s => s.id === slotId);
    return slot ? slot.enabled !== false : true;
  };

  // Get price from backend (in dollars)
  // TEMPORARY PRICE OVERRIDE - remove after production DB alignment
  const getTicketPrice = (slotId, categoryId) => {
    // TEMPORARY: Force hardcoded prices for production until DB is aligned
    if (USE_TEMP_PRICE_OVERRIDE) {
      return TEMP_PRICE_OVERRIDE[categoryId] ?? 0;
    }
    
    if (ticketAvailability && ticketAvailability[slotId]) {
      const slotData = ticketAvailability[slotId];
      const codeMap = { "A": "adult", "B": "kids", "C": "infants" };
      const code = codeMap[categoryId] || categoryId.toLowerCase();
      
      if (slotData.ticket_types && slotData.ticket_types[code]) {
        return slotData.ticket_types[code].price || 0;
      }
    }
    // Fallback defaults
    const defaultPrices = { "A": 30, "B": 25, "C": 0 };
    return defaultPrices[categoryId] || 0;
  };

  const fetchEventData = async () => {
    try {
      const eventRes = await fetch(`${API}/events/vishu-2026`);
      if (eventRes.ok) {
        const eventData = await eventRes.json();
        setEvent(eventData);
      }
    } catch (error) {
      console.error("Error fetching event:", error);
    } finally {
      setLoading(false);
    }
  };

  const getCartKey = (slotId, categoryId) => `${slotId}-${categoryId}`;

  const updateCart = (slotId, categoryId, delta) => {
    const key = getCartKey(slotId, categoryId);
    setCart(prev => {
      const current = prev[key] || 0;
      const newQty = Math.max(0, Math.min(10, current + delta));
      if (newQty === 0) {
        const { [key]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [key]: newQty };
    });
  };

  const getCartQty = (slotId, categoryId) => {
    return cart[getCartKey(slotId, categoryId)] || 0;
  };

  const getTotalPrice = () => {
    return Object.entries(cart).reduce((total, [key, qty]) => {
      const [slotId, categoryId] = key.split("-");
      const price = getTicketPrice(slotId, categoryId) * 100; // Convert to cents
      return total + (price * qty);
    }, 0);
  };

  const getTotalTickets = () => {
    return Object.values(cart).reduce((sum, qty) => sum + qty, 0);
  };

  const handleCheckout = async () => {
    if (!customerInfo.name || !customerInfo.email || !customerInfo.phone) {
      toast.error("Please fill in your name, email and phone number");
      return;
    }

    if (getTotalTickets() === 0) {
      toast.error("Please select at least one ticket");
      return;
    }

    setCheckingOut(true);
    try {
      // Build tickets array with slot and category info - prices from backend
      const tickets = Object.entries(cart).map(([key, quantity]) => {
        const [slotId, categoryId] = key.split("-");
        const slot = SLOTS.find(s => s.id === slotId);
        const category = CATEGORIES.find(c => c.id === categoryId);
        const ticketPrice = getTicketPrice(slotId, categoryId);
        return {
          slot_id: slotId,
          slot_name: slot?.name,
          slot_time: slot?.time,
          category_id: categoryId,
          category_name: category?.name,
          description: category?.description,
          price_cents: ticketPrice * 100, // Convert dollars to cents
          quantity
        };
      });

      const response = await fetch(`${API}/checkout/create-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          event_id: event?.id,
          tickets_data: tickets,
          customer_name: customerInfo.name,
          customer_email: customerInfo.email,
          customer_phone: customerInfo.phone,
          origin_url: window.location.origin,
          total_amount: getTotalPrice()
        })
      });

      if (response.ok) {
        const data = await response.json();
        window.location.href = data.checkout_url;
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to create checkout session");
      }
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setCheckingOut(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="animate-spin w-8 h-8 border-4 border-kerala-green border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="text-center">
          <h1 className="font-heading text-3xl text-kerala-green mb-4">Event Not Found</h1>
          <p className="text-gray-600">The event you're looking for doesn't exist yet.</p>
        </div>
      </div>
    );
  }

  const eventDate = new Date(event.date_time);

  return (
    <div className="pt-20 min-h-screen bg-gradient-to-b from-kerala-cream via-white to-kerala-cream" data-testid="event-page">
      {/* Premium Hero */}
      <section className="relative py-20 bg-gradient-to-br from-kerala-green via-kerala-green to-emerald-800 overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23D4AF37' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")"}}>
        </div>
        <div className="section-container relative z-10">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 bg-kerala-gold/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <Star className="w-4 h-4 text-kerala-gold fill-kerala-gold" />
              <span className="text-kerala-gold text-sm font-medium tracking-wider uppercase">
                {eventDate.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
              </span>
            </div>
            <h1 className="font-heading text-5xl md:text-6xl text-white mb-8 leading-tight">
              {event.title}
            </h1>
            <div className="flex flex-wrap gap-8 text-white/90">
              <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
                <Calendar className="w-5 h-5 text-kerala-gold" />
                <span className="font-medium">{eventDate.toLocaleDateString()}</span>
              </div>
              <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
                <Clock className="w-5 h-5 text-kerala-gold" />
                <span className="font-medium">11:00 AM - 5:00 PM</span>
              </div>
              <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-lg">
                <MapPin className="w-5 h-5 text-kerala-gold" />
                <span className="font-medium">{event.venue || "London, Ontario"}</span>
              </div>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-kerala-cream to-transparent"></div>
      </section>

      {/* Main Content */}
      <section className="py-8">
        <div className="section-container">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              
              {/* Left Column - Tickets */}
              <div className="lg:col-span-2 space-y-6">

                {/* Premium Ticket Selection Card */}
                <div className="bg-white rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.08)] border border-kerala-gold/10 overflow-hidden">
                  <div className="bg-gradient-to-r from-kerala-green via-kerala-green to-emerald-700 p-5">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                        <Ticket className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h2 className="font-heading text-2xl text-white">Select Your Tickets</h2>
                        <p className="text-white/70 text-sm">Choose your preferred time slot</p>
                      </div>
                    </div>
                  </div>

                  <div className="divide-y divide-kerala-gold/10">
                    {SLOTS.filter(slot => isSlotVisible(slot.id)).map((slot) => (
                      <div key={slot.id}>
                        <button
                          onClick={() => setExpandedSlot(expandedSlot === slot.id ? null : slot.id)}
                          className="w-full px-6 py-5 flex items-center justify-between hover:bg-gradient-to-r hover:from-kerala-cream/50 hover:to-transparent transition-all duration-300"
                        >
                          <div className="flex items-center gap-4">
                            <div className="w-14 h-14 bg-gradient-to-br from-kerala-green to-emerald-700 rounded-xl flex items-center justify-center shadow-md">
                              <span className="text-white font-bold text-xl">{slot.id}</span>
                            </div>
                            <div className="text-left">
                              <h3 className="font-heading text-xl text-kerala-green">{slot.name}</h3>
                              <p className="text-gray-500 flex items-center gap-2">
                                <Clock className="w-4 h-4" />
                                {slot.time}
                              </p>
                            </div>
                          </div>
                          <ChevronDown className={`w-6 h-6 text-kerala-green transition-transform duration-300 ${expandedSlot === slot.id ? "rotate-180" : ""}`} />
                        </button>

                        {expandedSlot === slot.id && (
                          <div className="px-6 pb-5 bg-gradient-to-b from-kerala-cream/30 to-transparent">
                            <div className="space-y-3">
                              {CATEGORIES.map((category) => {
                                const qty = getCartQty(slot.id, category.id);
                                const ticketLabel = category.id === "A" ? "Adult" : category.id === "B" ? "Kids" : "Infants";
                                const ticketPrice = getTicketPrice(slot.id, category.id);
                                const priceDisplay = ticketPrice > 0 ? `$${ticketPrice.toFixed(2)}` : "FREE";
                                const maxRemaining = getRemainingTickets(slot.id, category.id);
                                const isUnlimited = maxRemaining === null || maxRemaining === undefined || maxRemaining >= 999;
                                const availableToAdd = isUnlimited ? 999 : maxRemaining - qty;
                                const showLimit = !isUnlimited && maxRemaining < 500;
                                const isSoldOut = !isUnlimited && maxRemaining <= 0;
                                return (
                                  <div
                                    key={category.id}
                                    className={`bg-white rounded-xl p-4 flex items-center justify-between shadow-sm border border-kerala-gold/10 hover:border-kerala-gold/30 hover:shadow-md transition-all duration-300 ${isSoldOut ? 'opacity-60' : ''}`}
                                  >
                                    <div className="flex items-center gap-4">
                                      <div className="w-12 h-12 bg-gradient-to-br from-kerala-gold/20 to-kerala-gold/10 rounded-lg flex items-center justify-center">
                                        <span className="text-kerala-green font-bold text-lg">{category.id}</span>
                                      </div>
                                      <div>
                                        <h4 className="font-semibold text-kerala-green text-lg">{ticketLabel}</h4>
                                        <p className="text-gray-500">{category.description} - {priceDisplay}</p>
                                        {/* Ticket count hidden from public - only show SOLD OUT */}
                                        {isSoldOut && (
                                          <p className="text-xs mt-1 text-red-500 font-semibold">
                                            SOLD OUT
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                    
                                    <div className="flex items-center gap-3">
                                      <Button
                                        variant="outline"
                                        size="icon"
                                        className="h-10 w-10 rounded-full border-2 border-kerala-green/30 hover:border-kerala-green hover:bg-kerala-green hover:text-white transition-all"
                                        onClick={(e) => { e.stopPropagation(); updateCart(slot.id, category.id, -1); }}
                                        disabled={qty === 0}
                                      >
                                        <Minus className="w-4 h-4" />
                                      </Button>
                                      <span className="w-10 text-center font-bold text-xl text-kerala-green">{qty}</span>
                                      <Button
                                        variant="outline"
                                        size="icon"
                                        className="h-10 w-10 rounded-full border-2 border-kerala-green/30 hover:border-kerala-green hover:bg-kerala-green hover:text-white transition-all"
                                        onClick={(e) => { e.stopPropagation(); updateCart(slot.id, category.id, 1); }}
                                        disabled={isSoldOut || availableToAdd <= 0}
                                      >
                                        <Plus className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right Sidebar */}
              <div className="lg:col-span-1 space-y-6">
                
                {/* Premium Order Summary */}
                <div className="sticky top-24 space-y-6">
                  <div className="bg-white rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.08)] border border-kerala-gold/10 overflow-hidden">
                    <div className="bg-gradient-to-r from-kerala-green via-kerala-green to-emerald-700 p-4">
                      <h3 className="font-heading text-xl text-white flex items-center gap-3">
                        <ShoppingCart className="w-5 h-5" />
                        Order Summary
                      </h3>
                    </div>

                    <div className="p-5">
                      {getTotalTickets() === 0 ? (
                        <div className="text-center py-8">
                          <div className="w-16 h-16 rounded-full bg-kerala-cream mx-auto mb-4 flex items-center justify-center">
                            <Ticket className="w-8 h-8 text-kerala-gold/50" />
                          </div>
                          <p className="text-gray-400">No tickets selected</p>
                          <p className="text-sm text-gray-300 mt-1">Choose a slot to begin</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {/* Ticket Holder Information */}
                          <div className="bg-kerala-cream/50 rounded-lg p-3 border border-kerala-gold/20">
                            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Ticket Holder</p>
                            <p className="font-semibold text-kerala-green">{customerInfo.name || "Enter name below"}</p>
                          </div>

                          {/* Tickets Summary */}
                          <div>
                            <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Tickets ({getTotalTickets()})</p>
                            {Object.entries(cart).map(([key, qty]) => {
                              const [slotId, categoryId] = key.split("-");
                              const slot = SLOTS.find(s => s.id === slotId);
                              const category = CATEGORIES.find(c => c.id === categoryId);
                              // Map category to ticket type labels
                              const ticketTypeLabel = category?.id === "A" ? "Adult" : category?.id === "B" ? "Kids" : "Infants";
                              const ticketPrice = getTicketPrice(slotId, categoryId);
                              const priceDisplay = ticketPrice > 0 ? `${qty} × $${ticketPrice.toFixed(2)}` : `${qty} × FREE`;
                              const lineTotal = ticketPrice * qty;
                              return (
                                <div key={key} className="flex justify-between items-center py-2 border-b border-kerala-gold/10">
                                  <div>
                                    <p className="font-medium text-kerala-green">{ticketTypeLabel}</p>
                                    <p className="text-sm text-gray-500">{slot?.name} ({slot?.time})</p>
                                    <p className="text-xs text-gray-400">{priceDisplay}</p>
                                  </div>
                                  <p className="font-semibold text-kerala-green">{lineTotal > 0 ? `$${lineTotal.toFixed(2)}` : "FREE"}</p>
                                </div>
                              );
                            })}
                          </div>

                          <div className="pt-4 border-t-2 border-kerala-gold/20">
                            <div className="flex justify-between items-center">
                              <span className="font-heading text-xl text-kerala-green">Total</span>
                              <span className="font-heading text-2xl text-kerala-green">${(getTotalPrice() / 100).toFixed(2)}</span>
                            </div>
                            <p className="text-sm text-gray-500 mt-1">{getTotalTickets()} ticket{getTotalTickets() !== 1 ? 's' : ''}</p>
                          </div>

                          <div className="space-y-3 pt-4">
                            <div>
                              <Label className="text-gray-700 font-medium">Ticket Holder Name <span className="text-red-500">*</span></Label>
                              <Input
                                value={customerInfo.name}
                                onChange={(e) => setCustomerInfo(prev => ({ ...prev, name: e.target.value }))}
                                placeholder="Your full name"
                                className="mt-1 border-kerala-gold/20 focus:border-kerala-green focus:ring-kerala-green/20"
                                data-testid="customer-name-input"
                              />
                            </div>
                            <div>
                              <Label className="text-gray-700 font-medium">Phone Number <span className="text-red-500">*</span></Label>
                              <Input
                                type="tel"
                                value={customerInfo.phone}
                                onChange={(e) => setCustomerInfo(prev => ({ ...prev, phone: e.target.value }))}
                                placeholder="647-xxx-xxxx"
                                className="mt-1 border-kerala-gold/20 focus:border-kerala-green focus:ring-kerala-green/20"
                                data-testid="customer-phone-input"
                              />
                            </div>
                            <div>
                              <Label className="text-gray-700 font-medium">Email <span className="text-red-500">*</span></Label>
                              <Input
                                type="email"
                                value={customerInfo.email}
                                onChange={(e) => setCustomerInfo(prev => ({ ...prev, email: e.target.value }))}
                                placeholder="your@email.com"
                                className="mt-1 border-kerala-gold/20 focus:border-kerala-green focus:ring-kerala-green/20"
                                data-testid="customer-email-input"
                              />
                            </div>
                          </div>

                          <Button
                            onClick={handleCheckout}
                            disabled={checkingOut || getTotalTickets() === 0}
                            className="w-full bg-gradient-to-r from-kerala-green to-emerald-700 hover:from-emerald-700 hover:to-kerala-green text-white py-6 rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5"
                          >
                            {checkingOut ? (
                              <span className="flex items-center gap-2">
                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                Processing...
                              </span>
                            ) : (
                              <span className="flex items-center gap-2">
                                <Check className="w-5 h-5" />
                                Proceed to Checkout
                              </span>
                            )}
                          </Button>

                          <p className="text-xs text-center text-gray-400 flex items-center justify-center gap-1">
                            <span>🔒</span> Secure payment via Stripe
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Support Section */}
                  <div className="bg-gradient-to-br from-white to-kerala-cream/50 rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.05)] border border-kerala-gold/10 p-5">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-kerala-gold to-yellow-500 flex items-center justify-center shadow-md">
                        <Heart className="w-5 h-5 text-white" />
                      </div>
                      <h4 className="font-heading text-lg text-kerala-green">Support This Event</h4>
                    </div>
                    <p className="text-gray-600 text-sm mb-4">Help make this event unforgettable</p>
                    <div className="space-y-3">
                      <Link to="/sponsorship/picnic">
                        <Button variant="outline" className="w-full border-2 border-kerala-green text-kerala-green hover:bg-kerala-green hover:text-white transition-all duration-300 rounded-xl py-5">
                          <Award className="w-4 h-4 mr-2" />
                          Become a Sponsor
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
